"use client";

/**
 * Analytics Hooks
 *
 * React hooks for tracking page views and user interactions.
 * All hooks are error-resilient and never affect user experience.
 */

import { useEffect, useRef, useCallback } from "react";
import {
  trackPlanCtaClicked,
  trackPlanViewed,
  trackCheckoutStarted,
  trackProgramViewed,
  trackWorkoutDayViewed,
  trackRecipeViewed,
  trackRecipeFilterApplied,
  trackCourseViewed,
  trackLessonStarted,
} from "./track-client";
import type {
  PlanCtaClickedProps,
  PlanViewedProps,
  CheckoutStartedProps,
  ProgramViewedProps,
  WorkoutDayViewedProps,
  RecipeViewedProps,
  RecipeFilterAppliedProps,
  CourseViewedProps,
  LessonStartedProps,
} from "./types";

/**
 * Hook to track plan selection page view
 * Fires once when the component mounts with the given props
 */
export function useTrackPlanViewed(props: PlanViewedProps | null): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (props && !tracked.current) {
      tracked.current = true;
      trackPlanViewed(props);
    }
  }, [props]);
}

/**
 * Returns a function to track a public plan CTA click
 */
export function useTrackPlanCtaClicked(): (
  props: PlanCtaClickedProps,
) => void {
  return useCallback((props: PlanCtaClickedProps) => {
    trackPlanCtaClicked(props);
  }, []);
}

/**
 * Returns a function to track checkout started
 */
export function useTrackCheckoutStarted(): (
  props: CheckoutStartedProps,
) => void {
  return useCallback((props: CheckoutStartedProps) => {
    trackCheckoutStarted(props);
  }, []);
}

/**
 * Hook to track workout day page view
 * Fires once when the component mounts with the given props
 */
export function useTrackWorkoutDayViewed(
  props: WorkoutDayViewedProps | null,
): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (props && !tracked.current) {
      tracked.current = true;
      trackWorkoutDayViewed(props);
    }
  }, [props]);
}

/**
 * Hook to track program page view
 * Fires once when the component mounts with the given props
 */
export function useTrackProgramViewed(props: ProgramViewedProps | null): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (props && !tracked.current) {
      tracked.current = true;
      trackProgramViewed(props);
    }
  }, [props]);
}

/**
 * Hook to track recipe page view
 * Fires once when the component mounts with the given props
 */
export function useTrackRecipeViewed(props: RecipeViewedProps | null): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (props && !tracked.current) {
      tracked.current = true;
      trackRecipeViewed(props);
    }
  }, [props]);
}

/**
 * Returns a function to track recipe filter changes
 */
export function useTrackRecipeFilter(): (
  props: RecipeFilterAppliedProps,
) => void {
  return useCallback((props: RecipeFilterAppliedProps) => {
    trackRecipeFilterApplied(props);
  }, []);
}

/**
 * Hook to track course page view
 * Fires once when the component mounts with the given props
 */
export function useTrackCourseViewed(props: CourseViewedProps | null): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (props && !tracked.current) {
      tracked.current = true;
      trackCourseViewed(props);
    }
  }, [props]);
}

/**
 * Hook to track lesson page view (lesson started)
 * Fires once when the component mounts with the given props
 */
export function useTrackLessonStarted(props: LessonStartedProps | null): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (props && !tracked.current) {
      tracked.current = true;
      trackLessonStarted(props);
    }
  }, [props]);
}
